import { useCallback, useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useApiFetch } from '../../lib/api'
import { Pagination, DEFAULT_PAGE_SIZE_OPTIONS } from '../../components/Pagination'
import { DetailDrawer, DrawerField, DrawerNodeField } from '../../components/DetailDrawer'
import { SortableTh, toggleSort, type SortState } from '../../components/mcp/analytics/SortableTh'
import { useTableState } from './useTableState'
import {
  ChannelBadge,
  DataCell,
  InvocationModeBadge,
} from '../../components/mcp/analytics/badges'
import { inlineArray, jsonArrayBlock, sortedForMode } from '../../lib/mcpAnalytics/arrays'
import { formatDateTime, type FormatOptions } from '../../lib/formatDateTime'
import { TabState, TabNoDataYet } from './TabStates'
import { MCP_ANALYTICS_BASE, buildFilterParams, withTableParams } from './api'
import { resolvePeriod, viewerTimeZone, type AnalyticsFilterState } from './useAnalyticsFilters'
import type {
  McpAnalyticsSearchActivity,
  McpAnalyticsSearchActivityListResponse,
} from '../../lib/types'

/* ─── Search Activity (MS-702) ───

   Inputs and results for `search` tool calls, plus a details slide-over.

   Two decisions worth keeping in view:

   * **Paginated**, unlike the prototype's flat list. The pattern is shared with
     Tool Invocations, and an unpaginated list of every search a tenant has ever
     run is unbounded at real volumes.
   * The Search-input cell renders through the same `inlineArray` the Description
     column uses. A batch row therefore shows its queries **in the table** rather
     than deferring to the drawer, so the column answers "what was searched" for
     every row instead of only for single calls. */

const COLUMNS = [
  { key: 'timestamp', labelKey: 'timestamp', descendingFirst: true },
  { key: 'user', labelKey: 'user', descendingFirst: false },
  { key: 'channel', labelKey: 'channel', descendingFirst: false },
  { key: 'callType', labelKey: 'callType', descendingFirst: false },
  { key: 'searchInput', labelKey: 'input', descendingFirst: false },
  { key: 'matchedOperations', labelKey: 'matched', descendingFirst: false },
] as const

export const DEFAULT_SORT: SortState = { col: 'timestamp', dir: 'desc' }
const DEFAULT_PAGE_SIZE = 10

/** What a restored table state may contain, derived from what this tab actually
 *  renders so the two cannot drift apart. */
const ALLOWED = {
  sortCols: COLUMNS.map(c => c.key),
  pageSizes: DEFAULT_PAGE_SIZE_OPTIONS,
}

export interface SearchActivityTabProps {
  connectionKey: string
  filters: AnalyticsFilterState
  filtersKey: unknown[]
  reloadToken: number
}

export function SearchActivityTab({
  connectionKey,
  filters,
  filtersKey,
  reloadToken,
}: SearchActivityTabProps) {
  const { t, i18n } = useTranslation()
  const apiFetch = useApiFetch()
  // Instants render in the viewer's zone and the interface language: changing the
  // language changes the month's name, never the moment it names.
  const dateOptions = { timeZone: viewerTimeZone(), locale: i18n.language }

  const [selected, setSelected] = useState<McpAnalyticsSearchActivity | null>(null)
  // Kept under its own key: the two tables sort on different columns, so one shared
  // sort would hand this tab a `description` column it does not render.
  const { sort, page, pageSize, setSort, setPage, setPageSize, resetPage } = useTableState(
    connectionKey,
    'search',
    { sort: DEFAULT_SORT, page: 1, pageSize: DEFAULT_PAGE_SIZE },
    ALLOWED,
  )

  const filtersSignature = JSON.stringify(filtersKey)
  useEffect(() => {
    resetPage()
  }, [filtersSignature, resetPage])

  const query = useQuery({
    queryKey: [
      'mcp-analytics',
      'search-activity',
      filtersKey,
      sort.col,
      sort.dir,
      page,
      pageSize,
      reloadToken,
    ],
    placeholderData: (prev) => prev,
    queryFn: () => {
      const params = withTableParams(
        buildFilterParams(filters, resolvePeriod(filters)),
        { sortCol: sort.col, sortDir: sort.dir, page, pageSize },
      )
      return apiFetch<McpAnalyticsSearchActivityListResponse>(
        `${MCP_ANALYTICS_BASE}/search-activity?${params.toString()}`,
      )
    },
  })

  const handleSort = useCallback(
    (colKey: string) => {
      const column = COLUMNS.find(c => c.key === colKey)
      setSort(toggleSort(sort, colKey, column?.descendingFirst ?? false))
    },
    [sort, setSort],
  )

  const items = query.data?.items ?? []
  const hasFilters =
    filters.users.length > 0 || filters.channels.length > 0 || filters.invocationModes.length > 0

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h3>{t('mcpAnalytics.detail.search.title')}</h3>
          <p>{t('mcpAnalytics.detail.search.subtitle')}</p>
        </div>
      </div>
      <div className="card-body">
        <TabState
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
        >
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={query.data?.totalCount ?? 0}
            totalPages={query.data?.totalPages ?? 0}
            itemsOnPage={items.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onResetSort={() => setSort(DEFAULT_SORT)}
            isSortDefault={sort.col === DEFAULT_SORT.col && sort.dir === DEFAULT_SORT.dir}
            renderCount={({ from, to, total }) =>
              t('mcpAnalytics.detail.search.pager.showing', { from, to, total })
            }
            emptyLabel={t('mcpAnalytics.detail.search.pager.empty')}
          />

          <table className="data-table">
            <thead>
              <tr>
                {COLUMNS.map(column => (
                  <SortableTh
                    key={column.key}
                    colKey={column.key}
                    label={t(`mcpAnalytics.detail.search.columns.${column.labelKey}` as const)}
                    sort={sort}
                    onSort={handleSort}
                    defaultsToDescending={column.descendingFirst}
                  />
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.eventId}>
                  <td>{formatDateTime(item.timestamp, dateOptions)}</td>
                  <td>{item.name}</td>
                  <td>
                    <ChannelBadge channel={item.channel} agentName={item.agentName} />
                  </td>
                  <td>
                    <InvocationModeBadge mode={item.invocationMode} />
                  </td>
                  <td>
                    <DataCell
                      value={inlineArray(sortedForMode(item.searchInput, item.invocationMode), {
                        quoted: true,
                      })}
                    />
                  </td>
                  <td>{item.matchedOperations}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-ghost audit-details-btn"
                      onClick={() => setSelected(item)}
                    >
                      {t('mcpAnalytics.detail.search.showDetails')}
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length + 1} style={{ color: 'var(--text-tertiary)', padding: 14 }}>
                    {hasFilters ? t('mcpAnalytics.detail.search.tableEmpty') : <TabNoDataYet />}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TabState>
      </div>

      <SearchDetailsDrawer entry={selected} onClose={() => setSelected(null)} dateOptions={dateOptions} />
    </div>
  )
}

function SearchDetailsDrawer({
  entry,
  onClose,
  dateOptions,
}: {
  entry: McpAnalyticsSearchActivity | null
  onClose: () => void
  dateOptions: FormatOptions
}) {
  const { t } = useTranslation()
  // Both payload blocks are plain JSON arrays — no wrapper object and no attribute
  // name, so the block shows the values and nothing else (MS-702 AC7). Batch entries
  // are alphabetical, matching the Description column on Tool Invocations.
  const searchInput = entry ? sortedForMode(entry.searchInput, entry.invocationMode) : []

  return (
    <DetailDrawer
      isOpen={entry !== null}
      title={t('mcpAnalytics.detail.search.drawer.title')}
      onClose={onClose}
      recordKey={entry?.eventId ?? null}
      testId="mcp-search-detail-drawer"
    >
      {entry && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '16px 20px 24px' }}>
          <DrawerField
            label={t('mcpAnalytics.detail.search.drawer.timestamp')}
            value={formatDateTime(entry.timestamp, dateOptions)}
          />
          <DrawerField
            label={t('mcpAnalytics.detail.search.drawer.user')}
            value={`${entry.name} · ${entry.email}`}
          />
          <DrawerNodeField label={t('mcpAnalytics.detail.search.drawer.channel')}>
            <ChannelBadge channel={entry.channel} agentName={entry.agentName} />
          </DrawerNodeField>
          <DrawerNodeField label={t('mcpAnalytics.detail.search.drawer.invocationMode')}>
            <InvocationModeBadge mode={entry.invocationMode} />
          </DrawerNodeField>
          <DrawerField
            label={t('mcpAnalytics.detail.search.drawer.input')}
            value={
              entry.invocationMode === 'batch'
                ? jsonArrayBlock(searchInput)
                : `"${searchInput[0] ?? ''}"`
            }
            block
          />
          <DrawerField
            label={t('mcpAnalytics.detail.search.drawer.results')}
            value={t('mcpAnalytics.detail.search.drawer.resultsValue', {
              count: entry.matchedOperations,
            })}
          />
          <DrawerField
            label={t('mcpAnalytics.detail.search.drawer.returned')}
            value={jsonArrayBlock(entry.returnedOperationKeys)}
            block
          />
        </div>
      )}
    </DetailDrawer>
  )
}
